// ════════════════════════════════════════════════════════════════════════
//  Rückfrage vor einem Vorgang, der etwas wegnimmt
//
//  Kein `window.confirm`: der Kasten des Browsers sieht weder aus noch klingt er
//  wie diese Anwendung, blockiert den Hauptthread und ist in Tests nicht
//  greifbar – der Entfernen-Weg in der Admin-App war deshalb überhaupt nicht
//  geprüft, ohne dass es jemandem auffiel.
//
//  Und keine Rückfrage an der Stelle des Knopfes: sie macht die Zeile im Moment
//  des Klicks länger, und alles darunter springt.
//
//  Ein echtes `<dialog>`, weil der Browser dafür schon alles mitbringt: eigene
//  Ebene über allem, Hintergrund, Escape. Nachgebaut wären das drei Dinge, die
//  man einzeln falsch machen kann.
//
//  ── Warum hier und nicht in einer App ──────────────────────────────────
//  Zuerst lag das Bedienteil in `apps/admin`, mit dem Vermerk, beim dritten
//  Vorkommen ein Paket daraus zu machen. Das dritte war die Codetabelle der
//  Verwaltung. Die Gestaltung bleibt trotzdem in `@otto/design` – die Klassen
//  stehen in `dashboard.css`, wie die übrigen Möbel beider Dashboards. Dieses
//  Paket liefert Verhalten, keine Farben.
// ════════════════════════════════════════════════════════════════════════

import { useEffect, useId, useRef, type ReactNode } from "react";

export function ConfirmDialog({
  open,
  title,
  confirmLabel,
  onConfirm,
  onCancel,
  children,
}: {
  readonly open: boolean;
  readonly title: string;
  /** Beschriftung des bestätigenden Knopfes – benennt die TAT, nicht „OK“. */
  readonly confirmLabel: string;
  readonly onConfirm: () => void;
  readonly onCancel: () => void;
  readonly children: ReactNode;
}) {
  const ref = useRef<HTMLDialogElement>(null);
  const cancelRef = useRef<HTMLButtonElement>(null);
  const titleId = useId();

  useEffect(() => {
    const dialog = ref.current;
    if (!dialog) return;

    if (open) {
      // jsdom 25 kennt `showModal` nicht. Ohne den Rückfall stünde der Dialog im
      // Test nie offen – und die Prüfungen, die es hier gerade erst gibt, liefen
      // ins Leere. Im Browser ist immer der erste Zweig dran.
      if (typeof dialog.showModal === "function") dialog.showModal();
      else dialog.setAttribute("open", "");

      // Der Fokus landet auf „Abbrechen“, nicht auf dem bestätigenden Knopf:
      // wer aus Versehen zweimal drückt, soll damit nichts entfernen.
      cancelRef.current?.focus();
    } else if (dialog.open) {
      if (typeof dialog.close === "function") dialog.close();
      else dialog.removeAttribute("open");
    }
  }, [open]);

  if (!open) return null;

  return (
    <dialog
      ref={ref}
      className="confirmDialog"
      aria-labelledby={titleId}
      // Escape löst im Browser `cancel` aus – abgefangen, damit der Dialog nicht
      // hinter React's Rücken zugeht und `open` weiter true stünde.
      onCancel={(event) => {
        event.preventDefault();
        onCancel();
      }}
      // Der Klick daneben trifft das `dialog`-Element selbst; alles Sichtbare
      // liegt im `<form>` darin.
      onClick={(event) => {
        if (event.target === ref.current) onCancel();
      }}
    >
      <form method="dialog" className="confirmDialogBody">
        <h2 id={titleId} className="confirmDialogTitle">
          {title}
        </h2>
        <div className="confirmDialogText">{children}</div>
        <div className="confirmDialogActions">
          <button
            type="button"
            ref={cancelRef}
            className="confirmDialogCancel"
            onClick={onCancel}
          >
            Abbrechen
          </button>
          <button type="button" className="confirmDialogConfirm" onClick={onConfirm}>
            {confirmLabel}
          </button>
        </div>
      </form>
    </dialog>
  );
}
